/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import StyledCreateProfile from "./StyledCreateProfile";
import { Box, Chip, Modal, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import TextFeild from "../../components/CommonComponents/TextFeild";
import { INPUT_LABELS } from "../../constants/lang";
import { fetchTags } from "../../store/reducers/createProfile";
import { useDispatch, useSelector } from "react-redux";
import { ToastNotifyError } from "../../components/CommonComponents/Toast/ToastNotify";
import AddIcon from "@mui/icons-material/Add";
import ImageCropper from "../../components/CommonComponents/ImageCroper";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { uploadImage } from "../../store/reducers/authentication/apiThunk";
import rollbar from "../../rollbarConfig";

const ACCEPT_OPTIONS = "image/png, image/jpeg, image/jpg, image/webp";

const Profile = ({
  control,
  errors,
  selectedTags,
  setSelectedTags,
  onProfileImageChange,
}) => {
  const [tags, setTags] = useState([]);
  const dispatch = useDispatch();
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const inputFileRef = useRef(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const profileData = useSelector((state) => state?.createProfile?.userProfile);

  const handleImageCrop = ({ base64, file }) => {
    setCroppedImage(base64);
    setCroppedImageFile(file);
    setImageUrl(null);
    setOpenCropperModal(false);
  };

  const handleSelect = (tag) => {
    setSelectedTags((prevSelected) =>
      prevSelected.includes(tag)
        ? prevSelected.filter((t) => t !== tag)
        : [...prevSelected, tag]
    );
  };

  const handleCancelCropper = () => {
    setOpenCropperModal(false);
    setImageUrl(null);
  };

  useEffect(() => {
    if (profileData?.brand_image) {
      setProfileImage(profileData?.brand_image);
      setCroppedImage(profileData?.brand_image);
    }
    
    if (profileData?.tags && Array.isArray(profileData.tags)) {
      const formattedTags = profileData.tags.map(tag => {
        return tags.find(t => t.id === (tag.id || tag));
      }).filter(Boolean);
      
      setSelectedTags(formattedTags);
    }
  }, [profileData, tags]);

  useEffect(() => {
    const uploadDataImage = async () => {
      try {
        const response = await dispatch(uploadImage(croppedImageFile));
        const { key, uploadResponse } = response?.payload;
        const { status } = uploadResponse || {};
        if (status === 200) {
          const finalImage = process.env.REACT_APP_S3_IMAGES_URL + key;
          setProfileImage(finalImage);
        }
      } catch (err) {
        console.error(err);
        rollbar.error("Error message", err);
      }
    };
    if (croppedImageFile !== null) {
      uploadDataImage();
    }
  }, [croppedImageFile]);

  
  useEffect(() => {
    onProfileImageChange(profileImage);
  }, [profileImage]);

  useEffect(() => {
    const getTags = async () => {
      try {
        const res = await dispatch(fetchTags());
        const { data, status, message } = res?.payload;
        if (status) {
          setTags(data);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        rollbar.error("Error message", error);
        console.error("Login failed:", error);
      }
    };

    getTags();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file) return;
      if (!ACCEPT_OPTIONS.includes(file.type)) {
        ToastNotifyError("Invalid file type! Please select a PNG, JPEG, JPG, or WEBP image.");
        e.target.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        ToastNotifyError("File size must be less than 5MB");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
        setOpenCropperModal(true);
        e.target.value = "";
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <StyledCreateProfile>
      <Box className='header'>
        <Box className='create-profile-header'>Create Profile</Box>
        <Box className='create-profile-sub-header'>
          Create your profile and take the first step to success
        </Box>
      </Box>

      <Box>
        <Box className='topic-header'>Basic Information</Box>
        <Box className='image-wrap'>
          <Box
            className={`${croppedImage ? "profile-picture-image" : "profile-picture"}`}
            onClick={() => {
              if (!croppedImage) {
                inputFileRef.current.click();
              }
            }}
          >
            {croppedImage ? (
              <Box className='cropped-image-wrap'>
                <img src={croppedImage} alt='cropped-img' className='cropped-image' />
                <Box
                  className='image-close'
                  onClick={() => {
                    setCroppedImage(null);
                    setImageUrl(null);
                    setProfileImage(null);
                  }}
                >
                  <CloseRoundedIcon />
                </Box>
              </Box>
            ) : (
              <AddIcon className='add-icon' />
            )}
          </Box>
          <Typography className='add-image-text'>Add Brand Image</Typography>
        </Box>
        <input
          type='file'
          ref={inputFileRef}
          accept={"image/png, image/jpeg"}
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
      </Box>
      <Box className='create-profile-textfeilds'>
        <Box className='create-profile-store'>
          <Box className='text-feilds'>
            <Controller
              name='brandName'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='brandName'
                  required
                  label={INPUT_LABELS.brandName}
                  placeholder='Brand name'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.brandName}
                  helperText={errors.brandName?.message}
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='website'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='website'
                  required
                  label={INPUT_LABELS.website}
                  placeholder='Website'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.website}
                  helperText={errors.website?.message}
                />
              )}
            />
          </Box>
        </Box>
        <Box className='create-profile-store'>
          <Box className='text-feilds'>
            <Controller
              name='instagram'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='instagram'
                  label={INPUT_LABELS.instagram}
                  placeholder='Enter a valid Instagram URL (https://)'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.instagram}
                  helperText={errors.instagram?.message}
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='other'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='other'
                  label={INPUT_LABELS.other}
                  placeholder='Enter your Other profile URL'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.other}
                  helperText={errors.other?.message}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box className='create-profile-topics'>
        <Box className='topic-header'>Brand description</Box>
        <Box className='create-textfeilds'>
          <Controller
            name='brandWriteUp'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='brandWriteUp'
                required
                label={INPUT_LABELS.brandWriteUp}
                placeholder="Provide a concise description of the brand's mission and values"
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.brandWriteUp}
                helperText={errors.brandWriteUp?.message}
              />
            )}
          />
          <Controller
            name='productsProcesses'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                is='productsProcesses'
                label={INPUT_LABELS.productsProcesses}
                placeholder="Describe the brand's offerings or processes"
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.productsProcesses}
                helperText={errors.productsProcesses?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Box className='create-profile-tags'>
        <Box className='topic-header'>Tags</Box>
        <Box className='tags-component'>
          {tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag.name}
              className={selectedTags.includes(tag) ? "selected-tag" : "tags"}
              onClick={() => handleSelect(tag)}
            />
          ))}
        </Box>
      </Box>
      <Box className='create-profile-topics'>
        <Box className='topic-header'>Designer Bio</Box>
        <Box className='create-textfeilds'>
          <Controller
            name='bio'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='bio'
                required
                label={INPUT_LABELS.bio}
                placeholder='Add information about designers'
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.bio}
                helperText={errors.bio?.message}
              />
            )}
          />
          <Controller
            name='favAboutNYC'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='favAboutNYC'
                label={INPUT_LABELS.favAboutNYC}
                placeholder='Share what you love most about New York City'
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.favAboutNYC}
                helperText={errors.favAboutNYC?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Modal
        open={openCropperModal}
        onClose={() => setOpenCropperModal(false)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "40%",
            height: "60%",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 3,
            padding: 2,
          }}
        >
          <Box
            className='image-crop-display'
            sx={{ height: "90%", width: "100%" }}
          >
            {imageUrl && (
              <ImageCropper
                src={imageUrl}
                onCancel={handleCancelCropper}
                onCropComplete={handleImageCrop}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </StyledCreateProfile>
  );
};

export default Profile;
